import { Auth } from 'aws-amplify';

const PAYMENT_API_BASE = 'https://api.gogamify.com/payment';

async function getAuthHeaders(): Promise<Record<string, string>> {
  const session = await Auth.currentSession();
  const token = session.getIdToken().getJwtToken();
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

export interface PaymentMethod {
  id: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  isDefault: boolean;
}

export interface PaymentMethodsResponse {
  success: boolean;
  data?: {
    customerId: string;
    defaultPaymentMethodId: string;
    paymentMethods: PaymentMethod[];
  };
  error?: string;
}

export interface SetupSessionResponse {
  success: boolean;
  data?: { url: string };
  error?: string;
}

export interface RemovePaymentMethodResponse {
  success: boolean;
  data?: { detached: boolean; newDefaultPaymentMethodId?: string };
  error?: string;
}

export interface SetDefaultResponse {
  success: boolean;
  data?: { defaultPaymentMethodId: string };
  error?: string;
}

export async function getPaymentMethods(
  orgId: string
): Promise<PaymentMethodsResponse> {
  const headers = await getAuthHeaders();
  const res = await fetch(
    `${PAYMENT_API_BASE}/methods?orgId=${encodeURIComponent(orgId)}`,
    { method: 'GET', headers }
  );
  return res.json();
}

export async function createSetupSession(
  orgId: string,
  successUrl: string,
  cancelUrl: string
): Promise<SetupSessionResponse> {
  const headers = await getAuthHeaders();
  const res = await fetch(`${PAYMENT_API_BASE}/setup-session`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ orgId, successUrl, cancelUrl }),
  });
  return res.json();
}

export async function removePaymentMethod(
  orgId: string,
  pmId: string
): Promise<RemovePaymentMethodResponse> {
  const headers = await getAuthHeaders();
  const res = await fetch(`${PAYMENT_API_BASE}/methods/${pmId}`, {
    method: 'DELETE',
    headers,
    body: JSON.stringify({ orgId }),
  });
  return res.json();
}

export async function setDefaultPaymentMethod(
  orgId: string,
  pmId: string
): Promise<SetDefaultResponse> {
  const headers = await getAuthHeaders();
  const res = await fetch(`${PAYMENT_API_BASE}/default`, {
    method: 'PUT',
    headers,
    body: JSON.stringify({ orgId, paymentMethodId: pmId }),
  });
  return res.json();
}

export interface SeatInfo {
  purchasedSeats: number;
  minimumSeats: number;
  activeUsers: number;
  availableSeats: number;
  customerType: 'seat_based' | 'fixed_rate';
  billingTerm: string;
  nextBillingDate: string | null;
  pricePerSeat: number;
  currency: string;
  subscriptionStatus: string;
  billingStatus: string;
  trialEndDate: string | null;
  cancelAtPeriodEnd: boolean;
  planType: 'starter' | 'pro' | 'enterprise';
  salesChannel: 'plg' | 'sales';
  selfServiceCancellation: boolean;
}

export interface SeatInfoResponse {
  success: boolean;
  data?: SeatInfo;
  error?: string;
}

export interface ProrationPreviewResponse {
  success: boolean;
  data?: {
    prorationAmount: number;
    prorationAmountFormatted: string;
    currency: string;
    description: string;
  };
  error?: string;
}

export interface AddSeatResponse {
  success: boolean;
  data?: { newQuantity: number; purchasedSeats: number; message: string };
  error?: string;
}

export interface Transaction {
  id: string;
  amount: number;
  currency: string;
  status: string;
  created: number;
  description: string;
  metadata: Record<string, string>;
  receiptUrl: string | null;
}

export interface TransactionsResponse {
  success: boolean;
  data?: {
    transactions: Transaction[];
    hasMore: boolean;
  };
  error?: string;
}

export async function getTransactions(
  orgId: string,
  limit: number = 25
): Promise<TransactionsResponse> {
  const headers = await getAuthHeaders();
  const res = await fetch(
    `${PAYMENT_API_BASE}/transactions?orgId=${encodeURIComponent(
      orgId
    )}&limit=${limit}`,
    { method: 'GET', headers }
  );
  return res.json();
}

export async function getSeatInfo(orgId: string): Promise<SeatInfoResponse> {
  const headers = await getAuthHeaders();
  const res = await fetch(`${PAYMENT_API_BASE}/seat-info`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ orgID: orgId }),
  });
  return res.json();
}

export async function getProrationPreview(
  orgId: string,
  additionalSeats: number
): Promise<ProrationPreviewResponse> {
  const headers = await getAuthHeaders();
  const res = await fetch(`${PAYMENT_API_BASE}/proration-preview`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ orgID: orgId, additionalSeats }),
  });
  return res.json();
}

export async function addSeat(
  orgId: string,
  quantity: number
): Promise<AddSeatResponse> {
  const headers = await getAuthHeaders();
  const res = await fetch(`${PAYMENT_API_BASE}/add-seat`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ orgID: orgId, quantity }),
  });
  return res.json();
}

export interface CancelSubscriptionResponse {
  success: boolean;
  data?: { status: string; message: string };
  error?: string;
}

export async function cancelSubscription(
  orgId: string,
  cancelImmediately: boolean = false
): Promise<CancelSubscriptionResponse> {
  const headers = await getAuthHeaders();
  const res = await fetch(`${PAYMENT_API_BASE}/cancel-subscription`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ orgID: orgId, cancelImmediately }),
  });
  return res.json();
}

export interface ValidateBillingTypeResponse {
  success: boolean;
  error?: string;
  data?: {
    valid: boolean;
    billingType?: string;
    message: string;
    stripeCustomerUrl?: string;
    stripeCustomerId?: string;
    subscriptionId?: string;
    subscriptionStatus?: string;
    currentQuantity?: number;
  };
}

export async function validateBillingType(
  orgId: string,
  billingType: string
): Promise<ValidateBillingTypeResponse> {
  const headers = await getAuthHeaders();
  const res = await fetch(`${PAYMENT_API_BASE}/validate-billing-type`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ orgID: orgId, billingType }),
  });
  return res.json();
}

// ─── Cancellation Flow APIs ─────────────────────────────────────────────────

export type ExitSurveyReason =
  | 'too_expensive'
  | 'not_enough_time'
  | 'too_many_issues'
  | 'didnt_match_advertised';

export interface SubmitExitSurveyResponse {
  success: boolean;
  data?: { message: string };
  error?: string;
}

export async function submitExitSurvey(
  orgId: string,
  reason: ExitSurveyReason,
  freeText?: string
): Promise<SubmitExitSurveyResponse> {
  const headers = await getAuthHeaders();
  const res = await fetch(`${PAYMENT_API_BASE}/exit-survey`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ orgID: orgId, reason, freeText }),
  });
  return res.json();
}

export interface ApplyDiscountResponse {
  success: boolean;
  data?: { discountPercent: number; message: string };
  error?: string;
}

export async function applyDiscount(
  orgId: string,
  discountPercent: number
): Promise<ApplyDiscountResponse> {
  const headers = await getAuthHeaders();
  const res = await fetch(`${PAYMENT_API_BASE}/apply-discount`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ orgID: orgId, discountPercent }),
  });
  return res.json();
}

export interface ExtendTrialResponse {
  success: boolean;
  data?: { newTrialEndDate: string; bonusCredits?: number; message: string };
  error?: string;
}

export async function extendTrial(
  orgId: string,
  days: number,
  bonusCredits?: number
): Promise<ExtendTrialResponse> {
  const headers = await getAuthHeaders();
  const res = await fetch(`${PAYMENT_API_BASE}/extend-trial`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ orgID: orgId, days, bonusCredits }),
  });
  return res.json();
}

export interface ReactivateSubscriptionResponse {
  success: boolean;
  data?: { status: string; message: string };
  error?: string;
}

export async function reactivateSubscription(
  orgId: string
): Promise<ReactivateSubscriptionResponse> {
  const headers = await getAuthHeaders();
  const res = await fetch(`${PAYMENT_API_BASE}/reactivate-subscription`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ orgID: orgId }),
  });
  return res.json();
}

export interface SwitchBillingTermResponse {
  success: boolean;
  data?: { newTerm: string; message: string };
  error?: string;
}

export async function switchBillingTerm(
  orgId: string,
  newTerm: 'monthly' | 'yearly'
): Promise<SwitchBillingTermResponse> {
  const headers = await getAuthHeaders();
  const res = await fetch(`${PAYMENT_API_BASE}/switch-billing-term`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ orgID: orgId, newTerm }),
  });
  return res.json();
}

export interface PauseSubscriptionResponse {
  success: boolean;
  data?: { status: string; resumesAt: string; message: string };
  error?: string;
}

export async function pauseSubscription(
  orgId: string,
  months: number = 3
): Promise<PauseSubscriptionResponse> {
  const headers = await getAuthHeaders();
  const res = await fetch(`${PAYMENT_API_BASE}/pause-subscription`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ orgID: orgId, months }),
  });
  return res.json();
}

export interface EstimateCreditsResponse {
  success: boolean;
  data?: { granted: number; used: number; remaining: number };
  error?: string;
}

export async function getEstimateCredits(
  orgId: string
): Promise<EstimateCreditsResponse> {
  // Uses the existing stripePaymentProcessor GraphQL mutation — same as GeneralConfig
  try {
    const { API, graphqlOperation } = await import('@aws-amplify/api');
    const mutation = /* GraphQL */ `
      mutation StripePaymentProcessor($request: AWSJSON!) {
        stripePaymentProcessor(request: $request)
      }
    `;
    const request = JSON.stringify({
      operation: 'getOrgEntitlements',
      orgID: orgId,
    });
    const result = (await API.graphql(
      graphqlOperation(mutation, { request })
    )) as any;
    let parsed = result?.data?.stripePaymentProcessor;
    while (typeof parsed === 'string') {
      try {
        parsed = JSON.parse(parsed);
      } catch {
        break;
      }
    }
    return parsed ?? { success: false, error: 'No response' };
  } catch (e: any) {
    return { success: false, error: String(e) };
  }
}
